import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl';

import DatePicker from '../../components/DatePicker';
import Button from '../../components/Button';
import Header from '../../components/Header';
import InteractivePopup from '../../components/InteractivePopup';
import Icon from '../../components/Icon';
import PageTitle from '../../components/PageTitle';

import RepVisitsSearchMapView from '../RepVisitsSearchMapView';

import RepVisitsWeekOf from '../RepVisitsSearchPage/repvisits-week-of';
import { getWeekStart, getWeekEnd, formatDate } from '../RepVisitsSearchPage/date-utils';

import VisitsAvailability from '../VisitsAvailability';
import FairsAvailability from '../FairsAvailability';
import UpcomingFairsAvailability from '../UpcomingFairsAvailability';

import * as constants from '../Authorization/constants';
import { AVAILABILITY_FAIRS, AVAILABILITY_VISITS } from './constants';

import './styles.css';
import SubscriptionInActive from '../../components/SubscriptionInactive';
import SubscriptionActive from '../../components/SubscriptionActive';

const messages = defineMessages({
  prevWeek: {
    id: 'repvisits.search.prevWeek',
    defaultMessage: 'Previous week',
  },
  nextWeek: {
    id: 'repvisits.search.nextWeek',
    defaultMessage: 'Next week',
  },
  pickDate: {
    id: 'repvisits.search.pickDate',
    defaultMessage: 'Go To Date',
  },
  list: {
    id: 'repvisits.search.list',
    defaultMessage: 'List',
  },
  map: {
    id: 'repvisits.search.map',
    defaultMessage: 'Map',
  },
  schedule: {
    id: 'repvisits.availability.schedule',
    defaultMessage: 'Schedule',
  },
  showingWeek: {
    id: 'repvisits.availability.showingWeek',
    defaultMessage: 'Showing Week:',
  },
  allScheduledFairs: {
    id: 'repvisits.availability.allScheduledFairs',
    defaultMessage: 'Showing All Scheduled Fairs',
  },
});

const distance = '30mi';
const availabilityMap = 'map';
const availabilityList = 'list';

function getCurrentDate() {
  const currentDate = new Date();
  return formatDate(currentDate);
}

class HighSchoolAvailability extends React.PureComponent {
  static propTypes = {
    intl: PropTypes.shape({
      formatMessage: PropTypes.func.isRequired,
    }).isRequired,
    schoolId: PropTypes.string.isRequired,
    availability: PropTypes.string,
    week: PropTypes.arrayOf(String),
    toDate: PropTypes.instanceOf(Date).isRequired,
    fromDate: PropTypes.instanceOf(Date).isRequired,
    router: PropTypes.shape({
      push: PropTypes.func.isRequired,
    }).isRequired,
    onChangeDates: PropTypes.func.isRequired,
    scrollContextRef: PropTypes.object,
  };

  static defaultProps = {
    availability: AVAILABILITY_VISITS,
  };

  constructor(props, context) {
    super(props, context);
    const { fromDate, toDate } = this.props;
    this.state = {
      showDatePicker: false,
      view: availabilityList,
      fromDate,
      toDate,
    };
  }

  componentWillReceiveProps = /* istanbul ignore next */ ({ fromDate, toDate }) => {
    this.setState({ fromDate, toDate, showDatePicker: false });
  };

  onViewVisits = () => {
    this.routeAvailability(AVAILABILITY_VISITS);
  };

  onViewFairs = () => {
    this.routeAvailability(AVAILABILITY_FAIRS);
  };

  onViewList = () => {
    this.setState({ view: availabilityList });
  };

  onViewMap = () => {
    this.setState({ view: availabilityMap });
  };

  onViewPrevWeek = () => {
    this.changeWeek(-7);
  };

  onViewNextWeek = () => {
    this.changeWeek(7);
  };

  onDatePickerChanged = (day) => {
    this.props.onChangeDates(getWeekStart(day), getWeekEnd(day));
  };

  toggleDatePicker = () => {
    this.setState((prevState) => ({ showDatePicker: !prevState.showDatePicker }));
  };

  incrementDate = (date, days) => {
    const incrementedDate = new Date(date);
    incrementedDate.setDate(incrementedDate.getDate() + days);
    return incrementedDate;
  };

  changeWeek = (days) => {
    const fromDate = this.incrementDate(this.state.fromDate, days);
    const toDate = this.incrementDate(this.state.toDate, days);
    this.props.onChangeDates(fromDate, toDate);
  };

  routeAvailability = (availability) => {
    if (AVAILABILITY_VISITS === availability || AVAILABILITY_FAIRS === availability) {
      const { schoolId, router } = this.props;
      router.push({
        pathname: `/rep-visits/search/${schoolId}/${availability}`,
      });
    }
  };

  render() {
    const { intl, schoolId, availability, week, fromDate, toDate, scrollContextRef } = this.props;
    const { showDatePicker, view } = this.state;
    const today = getCurrentDate();

    const viewNavigation = AVAILABILITY_VISITS === availability && (
    <div styleName="view-navigation">
      <button
        type="button"
        onClick={this.onViewList}
        aria-label={intl.formatMessage(messages.list)}
        styleName={availabilityList === view ? 'view-selector-button view-selector-button-active' : 'view-selector-button'}
      >
        <FormattedMessage id="repvisits.search.listView" defaultMessage="List" />
        <Icon name="list" />
      </button>
      <SubscriptionActive anyOf={constants.REP_VISITS_SUBSCRIPTION}>
        <button
          type="button"
          onClick={this.onViewMap}
          styleName={availabilityMap !== view ? 'view-selector-button' : 'view-selector-button view-selector-button-active'}
          data-cy="repvisit-map-view-button"
        >
          <FormattedMessage id="repvisits.search.mapView" defaultMessage="Map" />
          <Icon name="map outline" />
        </button>
      </SubscriptionActive>
      <SubscriptionInActive allOf={constants.REP_VISITS_SUBSCRIPTION}>
        <button type="button" disabled="disabled" styleName="view-selector-button view-selector-button-disabled">
          <FormattedMessage id="repvisits.search.mapView" defaultMessage="Map" />
          <Icon name="map outline" disabled />
        </button>
      </SubscriptionInActive>
    </div>
    );

    const availabilityVisitTitle = (<PageTitle subTitle="Schedule Visits" title="RepVisits" category="Intersect" />);
    const availabilityFairTitle = (<PageTitle subTitle="Schedule Fairs" title="RepVisits" category="Intersect" />);

    return (
      <div>
        { AVAILABILITY_VISITS === availability && availabilityVisitTitle }
        { AVAILABILITY_FAIRS === availability && availabilityFairTitle }
        <Header as="h2" styleName="header" id="pageTitle">
          <FormattedMessage {...messages.schedule} />
        </Header>
        <Button.Group fluid>
          <Button
            onClick={this.onViewVisits}
            styleName={AVAILABILITY_VISITS !== availability ? 'visits-fairs' : 'visits-fairs-selected'}
          >
            <FormattedMessage id="hs-availability.visits" defaultMessage="Visits" />
          </Button>
          <Button
            onClick={this.onViewFairs}
            styleName={AVAILABILITY_FAIRS !== availability ? 'visits-fairs' : 'visits-fairs-selected'}
          >
            <FormattedMessage id="hs-availability.fairs" defaultMessage="Fairs" />
          </Button>
        </Button.Group>
        <div styleName="navigation">
          { AVAILABILITY_VISITS === availability && (
          <span>
            <span styleName="subHeader date-navigation-header">
              <FormattedMessage {...messages.showingWeek} />&nbsp;
            </span>
            <span styleName="subHeader" data-cy="showing-week-fromdate-todate">
              <RepVisitsWeekOf dateFrom={fromDate} dateTo={toDate} />
            </span>
          </span>
          )}
          { AVAILABILITY_FAIRS === availability && (
          <span styleName="subHeader">
            <FormattedMessage {...messages.allScheduledFairs} />&nbsp;
          </span>
          )}
          <span styleName="week-navigation">
            <button
              type="button"
              onClick={this.onViewPrevWeek}
              aria-label={intl.formatMessage(messages.prevWeek)}
              styleName="navigation-button week-navigation-button"
            >
              <Icon data-cy="repvisit-search-left-weeknavigation" name="triangle left" color="teal" styleName="week-navigation-icon" />
            </button>
            <button
              type="button"
              onClick={this.onViewNextWeek}
              aria-label={intl.formatMessage(messages.nextWeek)}
              styleName="navigation-button week-navigation-button"
            >
              <Icon data-cy="repvisit-search-right-weeknavigation" name="triangle right" color="teal" styleName="week-navigation-icon" />
            </button>
          </span>
          <InteractivePopup
            on="click"
            position="top center"
            open={showDatePicker}
            onOpen={this.toggleDatePicker}
            onClose={this.toggleDatePicker}
            trigger={(
              <Button
                size="tiny"
                floated="right"
                labelPosition="right"
                styleName="navigation-button"
                icon={<Icon name="calendar outline" />}
                content={intl.formatMessage(messages.pickDate)}
              />
)}
          >
            <DatePicker
              enableOutsideDays
              initialMonth={fromDate}
              selectedDays={{ from: fromDate, to: toDate }}
              onDayClick={this.onDatePickerChanged}
            />
          </InteractivePopup>
          { viewNavigation }
        </div>
        { AVAILABILITY_FAIRS === availability && <FairsAvailability id={schoolId} fromDate={today} />}
        <SubscriptionActive anyOf={constants.REP_VISITS_SUBSCRIPTION}>
          { AVAILABILITY_FAIRS === availability &&
            <UpcomingFairsAvailability id={schoolId} distance={distance} fromDate={today} numSchoolsWithinProximity={25} />}
        </SubscriptionActive>
        { AVAILABILITY_VISITS === availability && view === availabilityList && (
        <VisitsAvailability
          id={schoolId}
          distance={distance}
          week={week}
          today={today}
          fromDate={week[0]}
          toDate={week[4]}
          numSchoolsWithinProximity={10}
          scrollContextRef={scrollContextRef}
        />
        )}
        { AVAILABILITY_VISITS === availability && view === availabilityMap && (
        <div styleName="bottom">
          <RepVisitsSearchMapView hsId={schoolId} />
        </div>
        )}
      </div>
    );
  }
}

export default injectIntl(HighSchoolAvailability);
